import React, { useEffect, useRef, useState } from 'react'

import {
  Badge,
  Button,
  FormFooter,
  Icon,
  Popover,
  PopoverFormContainer,
  SelectDisplay,
  SideDrawer,
  Tooltip,
  type TooltipProps,
  useMediaQuery,
} from '../../module'
import styles from './_sort-column.module.scss'
import { type ActiveTableFilterProps } from '../TableComponents/TableFilter/TableFilter'
import type TableFilter from '../TableComponents/TableFilter/TableFilter'
import { type SelectDisplayOption } from '../../components/Selects/SelectDisplay/SelectDisplay'
import { useIsClient } from '../../hooks'
import ColumnFilter, {
  type FilterOptions,
} from '../TableComponents/ColumnFilter/ColumnFilter'
import { c } from '../../translations/LibraryTranslationService'
import { createTwoLineLabel } from '../../services/tableHelperFunctions'

type TableFilterProps = React.ComponentProps<typeof TableFilter>

type SortColumnOptionsProps = {
  name: string
  label: string
}
type optionsFilterHeader = SelectDisplayOption & {
  type?: 'currency' | 'percentage'
}
type SortColumnTooltipProps = {
  content: TooltipProps['tooltipContent']
  position?: TooltipProps['position']
}

export type SortByProps = {
  /** Value used to sort by */
  prop: string
  /** `True` === 'asc' && `False` === 'desc' */
  flip: boolean
  /** Should use the `:lowercase` param */
  lowerCaseParam?: boolean
}

type ChangeValueProps = {
  id: number
  text: string
  value: string | number
  keyField: string
  label: string
  condition: string
  type: 'currency' | 'percentage'
  actualValue?: string | number
}

export type ActiveFilterProps = {
  [key: string]: ChangeValueProps
}

export type SortColumnProps<OptionItem extends SortColumnOptionsProps> =
  | SortColumnPropsSingleValue
  | SortColumnPropsMultipleValues<OptionItem>

type SortColumnFilterBaseProps = {
  headerText: string
  /** Temporary active sort option for secondary filter. This is needed to keep track of changes to the sort before the sort has been applied. (TODO: this prop needs to be required after all instances in consuming apps have been updated) */
  secondarySortProp?: string
  /**
   * Content inside of the filter
   * Making this prop optional as we will be deprecating this prop once we add all the `type` of filter used across all other apps
   */
  children?: React.ReactNode
  /**
   * Callback function to execute if Apply button is clicked from the filter popup.
   * @param headerText - Name to the popover filter header.
   * @param selectedColumnFilter - Values of the currently selected options in a object form.
   * @returns void
   */
  callout: (headerText: string, selectedColumnFilter: ActiveFilterProps) => void
  /** Callout invoked when the filter is closed. This helps us to clear any unsaved changes to the filter. (TODO: this prop needs to be required after all instances in consuming apps have been updated) */
  closeCallout?: (headerText: string) => void
  /** Callout invoked when active sortProp changes. This callout provides sortProp which is currently active. This helps us to keep track of intermediate changes to sortProp before the filter has been applied. (TODO: this prop needs to be required after all instances in consuming apps have been updated) */
  onSortChange?: (sortPropName: string) => void
  disabled: boolean
  /**
   * Type of a column filter. Currently we support input only of type number
   * TODO: Make this prop required once we remove above `children` prop
   */
  type?: 'number'
  /**
   * Unique column key to perform the sort
   * TODO: Make this prop required once we remove above `children` prop
   */
  tableKey?: string
  /** Optional callback triggers when any change occurs in the toggle options or on input change */
  columnFilterCallback?: (activeFilter: ActiveFilterProps) => void
  /** Optional object to display the default selected filter values */
  defaultFilter?: {
    selectedFilterOption: FilterOptions
    value: number | string
  }
  /** Optionally enable filtering by sub-metrics (the change values) */
  enableFilterByChangeValues?: boolean
  /** Callback function to execute if the reset button is clicked in the filter popover. This is optional for now until all instances in the consuming apps have been updated. */
  resetCallback?: (headerText: string) => void
}

type ColumnFilterCurrency = SortColumnFilterBaseProps & {
  /**
   * Display currency symbol left aligned inside number input
   * TODO: Make this prop required once we remove above `children` prop
   */
  textFieldType?: 'currency'
  /** Display currency symbol left aligned inside number input (e.g: $) */
  currencySymbol: string
}

type ColumnFilterPercentage = SortColumnFilterBaseProps & {
  /**
   * Display percentage symbol left aligned inside number input
   * TODO: Make this prop required once we remove above `children` prop
   */
  textFieldType?: 'percentage'
  currencySymbol?: never
}

type SortColumnFilterProps = ColumnFilterPercentage | ColumnFilterCurrency

type SortColumnPropsBase = {
  /** Label that is displayed for the table column */
  label: string
  /** Function to update sort direction of the column while also setting the column to active */
  sorter: (arg: {
    activeColumn: SortByProps['prop']
    direction: boolean
    lowerCaseParam?: SortByProps['lowerCaseParam']
  }) => void
  /** Optional class  */
  customClass?: string
  /** Optionally add custom JSX. This will be rendered to the right of the column header. */
  columnHeaderSubContent?: React.ReactNode
  /** This determines the style of the label. It tells the user which column is currently being sorted.   */
  active: SortByProps
  /** Name of the label that we store locally in to the browser  */
  localStorageName: string
  /** This is used if we need to send back `lowercase` in the api params */
  lowerCaseParam?: boolean
  /** Optional tooltip to be displayed next to the label   */
  tooltip?: SortColumnTooltipProps
  /** This allows us to add a filter for a specific column. The filter is then applied to the table to help narrow down the results of the table. */
  filter?: SortColumnFilterProps
  /** Table filters that are currently in use. This is to be used with the `filter` prop. */
  activeFilters?: TableFilterProps['activeFilters']
  /** Optional prop to determine whether to allow sorting for the column */
  noSort?: boolean
  /** This prop will provide extra space for the column when the `showStickyClasses` boolean is `true` in our tables. */
  extraSpaceWhenStickyColumn?: boolean
  /** Optional boolean to add a beta tag to the header */
  beta?: boolean
  /** Used for displaying active custom filters if any */
  tableHeaderActiveFilters?: ActiveTableFilterProps
  /** Optional boolean to split the column labels into two lines */
  twoLineLabel?: boolean
}

type SortColumnPropsSingleValue = SortColumnPropsBase & {
  /** propName is used if there is only 1 sort value */
  propName: string
  options?: never
}

type SortColumnPropsMultipleValues<OptionItem> = SortColumnPropsBase & {
  propName?: never
  /** options are used if there are more than 1 sort values */
  options: OptionItem[]
}

type SortFuncProps = {
  callback?: () => void
  keyName: string
}

const SortColumn = <OptionItem extends SortColumnOptionsProps>({
  label,
  propName,
  options,
  sorter,
  customClass = '',
  active,
  localStorageName,
  lowerCaseParam,
  tooltip,
  filter,
  activeFilters,
  noSort,
  columnHeaderSubContent,
  extraSpaceWhenStickyColumn,
  beta,
  tableHeaderActiveFilters,
  twoLineLabel,
}: SortColumnProps<OptionItem>): React.JSX.Element => {
  const [selectedFilterOption, setSelectedFilterOption] = useState<
    optionsFilterHeader | undefined
  >(options?.[0]) //This state is necessary to keep track of the currently selected filter option, which will be passed to the callback.
  const [activeSortProp, setActiveSort] = useState(
      filter?.secondarySortProp ?? '',
    ),
    [activeSortLabel, setActiveSortLabel] = useState(''),
    [columnFilterData, setColumnFilterData] = useState<ActiveFilterProps>({}),
    activeOption = options?.map((opt) => opt.name).includes(active.prop),
    activePropName = propName === active.prop,
    propNameCheck = options ? activeOption : activePropName,
    screenIsMdMin = useMediaQuery({ type: 'min', breakpoint: 'md' }),
    [inputValue, setInputValue] = useState<{
      value: number | string
      actualValue: number | string
    }>()
  const prevAppliedFilter = useRef(activeFilters ?? tableHeaderActiveFilters),
    [isDrawerOpen, setDrawerOpen] = useState(false)

  const sort = ({ callback, keyName }: SortFuncProps) => {
    if (sorter) {
      // Checking to see if we have options or propName to determine which of these should check against active.flip
      const dir = propNameCheck ? !active.flip : active.flip
      localStorage.setItem(
        localStorageName,
        JSON.stringify({
          prop: keyName,
          flip: dir,
          lowerCaseParam: lowerCaseParam,
        }),
      )

      sorter({
        activeColumn: keyName,
        direction: dir,
        lowerCaseParam,
      })
      callback?.()
    }
  }
  const closeFilter = () => {
    /**
     * Right now we only support custom filter to the main column i.e. first item from the options list.
     * TODO: Change setActiveSort value to dynamic once we start supporting filter for all the options of Sort By.
     */
    setActiveSort(options?.[0]?.name ?? '')
  }

  const isCurrentActiveSort = options
    ? options.some((opt) => opt.name === active.prop)
    : propName === active.prop

  const callout = (option: SelectDisplayOption) => {
    setActiveSortLabel(option.label)
    setActiveSort(option.name)
    filter?.onSortChange?.(option.name)
  }
  const cancelFilter = () => {
    setActiveSort(active.prop)
  }

  //Since options prop is static, we can use useRef to store the options and avoid unnecessary re-renders
  const optionsRef = useRef(options)
  useEffect(() => {
    setActiveSort(
      filter?.secondarySortProp
        ? filter?.secondarySortProp
        : (optionsRef.current?.filter((opt) => opt?.name === active.prop)?.[0]
            ?.name ?? ''),
    )
  }, [active.prop, filter?.secondarySortProp])

  const applyFilter = () => {
    const defaultSelectedLabel =
      options?.filter((e) => e.name === activeSortProp)?.[0]?.label ?? ''
    const defaultSelectedName =
      options?.filter((e) => e.name === activeSortProp)?.[0]?.name ?? ''

    let selectedColumnFilter = {
      [defaultSelectedLabel]: {
        id: 1,
        text: '>',
        condition: 'gt',
        value: inputValue?.value || '',
        keyField: defaultSelectedName,
        label: `${defaultSelectedLabel} > `,
        type: filter?.textFieldType || 'currency',
      },
    }

    if (!inputValue?.value) {
      setColumnFilterData({})
    } else if (Object.keys(columnFilterData).length > 0) {
      selectedColumnFilter = columnFilterData
    }
    filter?.callout(
      filter?.enableFilterByChangeValues
        ? (selectedFilterOption?.label ?? '')
        : filter.headerText,
      selectedColumnFilter,
    )
    !noSort &&
      !!activeSortProp &&
      !(activeSortProp === active.prop) &&
      sort({ keyName: activeSortProp })
    closeFilter()
  }
  const isDisableApply = noSort
    ? filter?.disabled
    : filter?.disabled &&
      ((!!activeSortProp && activeSortProp === active.prop) || !activeSortProp)

  const filterContentProps = {
    filter: filter,
    label: label,
    noSort: noSort,
    callout: callout,
    activeSortProp: activeSortProp,
    tableKey: filter?.tableKey,
    textFieldType: filter?.textFieldType,
    activeSortLabel: activeSortLabel,
    columnFilterCallback: filter?.columnFilterCallback,
    setColumnFilterData: setColumnFilterData,
    inputValue: inputValue,
    setInputValue: setInputValue,
    enableFilterByChangeValues: filter?.enableFilterByChangeValues,
    selectedFilterOption: selectedFilterOption,
    setSelectedFilterOption: setSelectedFilterOption,
  }

  const appliedFilters = activeFilters ?? tableHeaderActiveFilters

  // This useEffect is to clear the filter input value state when the filter is removed
  useEffect(() => {
    if (options) {
      const optionLabels = new Set(options.map((op) => op.label))

      const presentInPrevious = Object.keys(
        prevAppliedFilter.current ?? {},
      ).some((key) => optionLabels.has(key))

      const presentInCurrent = Object.keys(appliedFilters ?? {}).some((key) =>
        optionLabels.has(key),
      )

      if (presentInPrevious && !presentInCurrent) {
        setInputValue({ value: '', actualValue: '' })
      }

      prevAppliedFilter.current = appliedFilters
    }
  }, [appliedFilters, options])

  return (
    <div
      className={`${styles.container} ${customClass} ${
        extraSpaceWhenStickyColumn ? styles.extraSpace : ''
      }`}
    >
      {filter &&
      appliedFilters &&
      Object.keys(appliedFilters).find((af) =>
        filter?.enableFilterByChangeValues
          ? options?.some(
              (o) =>
                af.toLowerCase().trim() === o?.label.toLocaleLowerCase().trim(),
            )
          : af.toLowerCase().trim() === label.toLowerCase().trim(),
      ) ? (
        <span className={styles.activeFilterDot} />
      ) : null}

      {filter ? (
        screenIsMdMin ? (
          <Popover
            popoverContent={({ setVisible }) => (
              <PopoverFormContainer
                width='auto'
                header={filter.headerText}
                footerChildren={
                  <FilterFormFooter
                    callout={applyFilter}
                    disabled={isDisableApply}
                    closeCallout={filter.closeCallout}
                    headerText={filter.headerText}
                    cancelFilter={cancelFilter}
                    resetCallout={filter?.resetCallback}
                    setVisible={setVisible}
                  />
                }
              >
                <FilterContent
                  {...filterContentProps}
                  // @ts-expect-error Types are incompatible. This needs to be looked into.
                  options={options}
                  setVisible={setVisible}
                />
              </PopoverFormContainer>
            )}
            arrowColor='lighter-gray'
            noPadding
            position='bottom'
            maxWidth='auto'
          >
            {({ visible, setVisible }) => (
              <SortHeaderLabel
                label={label}
                active={propNameCheck}
                twoLineLabel={twoLineLabel}
                callout={() => setVisible(!visible)}
              />
            )}
          </Popover>
        ) : (
          <>
            <SortHeaderLabel
              label={label}
              active={propNameCheck}
              twoLineLabel={twoLineLabel}
              callout={() => setDrawerOpen(true)}
            />
            <SideDrawer
              isOpen={isDrawerOpen}
              closeCallout={() => setDrawerOpen(false)}
              headerContent={filter.headerText}
              footerContent={
                <div className={styles.filterFormFooterContainer}>
                  <FilterFormFooter
                    callout={applyFilter}
                    disabled={isDisableApply}
                    closeCallout={filter.closeCallout}
                    headerText={filter.headerText}
                    cancelFilter={cancelFilter}
                    resetCallout={filter?.resetCallback}
                    setVisible={setDrawerOpen}
                  />
                </div>
              }
            >
              <FilterContent
                {...filterContentProps}
                // @ts-expect-error Types are incompatible. This needs to be looked into.
                options={options}
              />
            </SideDrawer>
          </>
        )
      ) : (
        <>
          {options && (
            <OptionsPopover options={options} sort={sort} active={active}>
              {({ showPopover }) => (
                <SortHeaderLabel
                  label={label}
                  callout={() => {
                    showPopover()
                  }}
                  active={propNameCheck}
                  twoLineLabel={twoLineLabel}
                />
              )}
            </OptionsPopover>
          )}
          {propName && (
            <SortHeaderLabel
              label={label}
              callout={() => {
                sort({ keyName: propName })
              }}
              active={propNameCheck}
              twoLineLabel={twoLineLabel}
            />
          )}
        </>
      )}
      {beta ? <Badge children='Beta' color='lavender' /> : null}
      {tooltip && (
        <Tooltip
          tooltipContent={tooltip.content}
          position={tooltip.position ? tooltip.position : 'bottom'}
        >
          <Icon icon='info' color='dark-blue' iconSize='12px' />
        </Tooltip>
      )}
      {columnHeaderSubContent ? columnHeaderSubContent : null}
      {/* TODO: make icon click use the OptionsPopover as well */}
      {!noSort && (
        <Button
          as='unstyled'
          style={{ display: 'flex', justifyContent: 'flex-start' }}
          onClick={() =>
            sort({
              ...(options
                ? {
                    keyName: options?.some((el) => el.name === active?.prop)
                      ? active?.prop
                      : options?.[0].name,
                  }
                : { keyName: propName ?? '' }),
            })
          }
        >
          <Icon
            icon='carat'
            className={`${isCurrentActiveSort && active.flip ? styles.flip : ''}`}
            color={activePropName || activeOption ? 'dark-purple' : 'purple'}
            iconSize='8px'
          />
        </Button>
      )}
    </div>
  )
}

export default SortColumn

type SortHeaderLabelProps = {
  label: string
  callout?: () => void
  active?: boolean
  twoLineLabel?: boolean
}

const SortHeaderLabel = ({
  label,
  callout,
  active,
  twoLineLabel,
}: SortHeaderLabelProps) => (
  <Button
    as='unstyled'
    className={`${styles.header} ${active ? styles.active : ''}`}
    onClick={callout}
  >
    {createTwoLineLabel(label, twoLineLabel)}
  </Button>
)

type OptionsPopoverProps<OptionItem extends SortColumnOptionsProps> = {
  options: OptionItem[]
  sort: ({ callback, keyName }: SortFuncProps) => void
  active: SortByProps
  children: (showPopover: { showPopover: () => void }) => React.ReactNode
}

const OptionsPopover = <OptionItem extends SortColumnOptionsProps>({
  options,
  sort,
  active,
  children,
}: OptionsPopoverProps<OptionItem>) => {
  const [activeProp, setActive] = useState(''),
    isClient = useIsClient()

  const callout = (option: SelectDisplayOption) => {
    setActive(option.name)
    sort({ keyName: option.name })
  }

  useEffect(() => {
    const activePropName =
      options?.filter((e) => e.name === active.prop)?.[0]?.name ?? ''
    setActive(activePropName)
  }, [active.prop, options])

  return (
    <Popover
      position='bottom'
      popoverContent={({ setVisible }) => (
        <SelectDisplay
          options={options}
          callout={(option) => {
            callout(option)
            setVisible(false)
          }}
          active={activeProp as keyof OptionItem}
        />
      )}
      noPadding
      header={{ headerText: c('sortBy') }}
      {...(isClient ? { appendTo: document.body } : {})}
    >
      {({ setVisible }) => (
        <span>{children({ showPopover: () => setVisible(true) })}</span>
      )}
    </Popover>
  )
}

type FilterFormFooterProps = {
  callout: () => void
  disabled?: boolean
  closeCallout?: (headerText: string) => void
  headerText: string
  /** Optional callback function to cancel the filter changes */
  cancelFilter?: () => void
  /** Optional callback function to reset the filter */
  resetCallout?: (headerText: string) => void
  /** Optional callback function to set the visibility of the filter */
  setVisible?: React.Dispatch<React.SetStateAction<boolean>>
}

const FilterFormFooter = ({
  callout,
  disabled,
  closeCallout,
  headerText,
  cancelFilter,
  resetCallout,
  setVisible,
}: FilterFormFooterProps) => {
  const close = () => {
    closeCallout?.(headerText)
    cancelFilter?.()
    setVisible?.(false)
  }

  return (
    <div className={styles.filterFormFooter}>
      <FormFooter
        {...(resetCallout
          ? {
              resetButtonProps: {
                onClick: () => {
                  resetCallout?.(headerText)
                },
              },
            }
          : {})}
        cancelButtonProps={{
          onClick: () => {
            close()
          },
        }}
        saveButtonProps={{
          children: c('apply'),
          disabled: disabled,
          onClick: () => {
            callout()
            close()
          },
        }}
      />
    </div>
  )
}

type FilterContentProps = {
  filter: SortColumnPropsBase['filter']
  noSort: SortColumnPropsBase['noSort']
  options: SelectDisplayOption[]
  callout: (option: SelectDisplayOption) => void
  activeSortProp: string
  tableKey: keyof SortColumnPropsBase['filter']
  textFieldType: 'currency' | 'percentage'
  columnFilterCallback: (activeFilter: ActiveFilterProps) => void
  activeSortLabel: string
  setColumnFilterData: React.Dispatch<React.SetStateAction<ActiveFilterProps>>
  inputValue: {
    value: number | string
    actualValue: number | string
  }
  setInputValue: React.Dispatch<
    React.SetStateAction<{
      value: number | string
      actualValue: number | string
    }>
  >
  selectedFilterOption?: optionsFilterHeader
  setSelectedFilterOption: React.Dispatch<
    React.SetStateAction<optionsFilterHeader>
  >
  enableFilterByChangeValues: SortColumnFilterBaseProps['enableFilterByChangeValues']
  setVisible?: React.Dispatch<React.SetStateAction<boolean>>
}

type FilterChildProps = {
  options?: SelectDisplayOption[]
  selectedFilterHeader?: optionsFilterHeader
  setSelectedFilterHeader?: React.Dispatch<
    React.SetStateAction<optionsFilterHeader>
  >
}

const FilterContent = ({
  filter,
  noSort,
  options,
  callout,
  activeSortProp,
  tableKey,
  textFieldType,
  columnFilterCallback,
  setColumnFilterData,
  inputValue,
  setInputValue,
  enableFilterByChangeValues,
  selectedFilterOption,
  setSelectedFilterOption,
  setVisible,
}: FilterContentProps): React.JSX.Element => {
  const hasSortAndOptions = !noSort && !!options

  return (
    <div
      className={`${styles.filterContainer} ${
        options ? styles.hasOptions : ''
      }`}
    >
      {filter?.type === 'number' ? (
        <ColumnFilter
          tableKey={tableKey || ''}
          prefix={textFieldType === 'currency' ? '$' : ''}
          suffix={textFieldType === 'percentage' ? '%' : ''}
          columnFilterCallback={columnFilterCallback}
          headerText={filter?.headerText || ''}
          textInputProps={{
            /**
             * Right now we only support custom filter to the main column i.e. first item from the options list.
             * TODO: Change labelText value to activeSortLabel ? activeSortLabel : filter?.headerText once we start supporting filter for all the options of Sort By.
             */
            labelText: filter?.headerText || '',
            value: inputValue?.value || '',
            type: 'number',
            placeholder: '0.00',
            numberFormat: {
              currencySymbol: filter?.currencySymbol ?? '',
              type: textFieldType,
            },
          }}
          setInputValue={setInputValue}
          defaultFilter={filter?.defaultFilter}
          setColumnFilterData={setColumnFilterData}
          options={options}
          enableFilterByChangeValues={enableFilterByChangeValues}
          selectedFilterOption={selectedFilterOption}
          setSelectedFilterOption={setSelectedFilterOption}
          setVisible={setVisible}
        />
      ) : (
        /**
         We will render children as they are for now because some applications may require filters that are of type other than number such as select or multiselect.
         */
        <div>
          {React.cloneElement(
            filter?.children as React.ReactElement<FilterChildProps>,
            {
              options: options,
              selectedFilterHeader: selectedFilterOption,
              setSelectedFilterHeader: setSelectedFilterOption,
            },
          )}
        </div>
      )}

      {hasSortAndOptions && (
        <>
          <div className={styles.divider} />
          <div>
            <div className={styles.sortByText}>{c('sortBy')}</div>
            <SelectDisplay
              options={options}
              callout={callout}
              active={activeSortProp as keyof SelectDisplayOption}
              hasBorder
            />
          </div>
        </>
      )}
    </div>
  )
}
